import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import ProfileInfo from './ProfileInfo';
import CustomerBookingHistory from './CustomerBookingHistory';
import CustomerReviews from './CustomerReviews';
import './CustomerDetails.css';

const MAROON = '#c4941a';

const TABS = [
  { key: 'profile-info',    label: 'Profile Info',    icon: 'bi-person'          },
  { key: 'booking-history', label: 'Booking History', icon: 'bi-calendar-check'  },
  { key: 'reviews',         label: 'Reviews',          icon: 'bi-star'            },
];

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile-info');

  useEffect(() => {
    getUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getUserDetails = async () => {
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/users/${id}` });
      if (res.data.status) setUser(res.data.data);
      else { toast.error(res.data.message); navigate('/admin/users'); }
    } catch {
      toast.error('Failed to load user details');
      navigate('/admin/users');
    } finally {
      setIsLoader(false);
    }
  };

  const handleToggleStatus = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: user?.status ? 'You want to restrict this account!' : 'You want to activate this account!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: MAROON,
      confirmButtonText: 'Yes',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await ApiService.request({ method: 'POST', url: `admin/users/status/${id}` });
      if (res.data.status) { getUserDetails(); toast.success(res.data.message); }
      else toast.error(res.data.message);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const avatarSrc = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=c4941a&color=fff&size=128`;

  if (isLoader && !user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Loader text="Loading…" size={40} />
      </div>
    );
  }

  if (!user) return null;

  const renderContent = () => {
    switch (activeTab) {
      case 'booking-history': return <CustomerBookingHistory userId={id} />;
      case 'reviews':         return <CustomerReviews userId={id} />;
      default:                return <ProfileInfo user={user} />;
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Breadcrumb toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {user.name || 'Customer Details'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/users" className="text-muted text-hover-primary">Customers</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">Details</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className={`btn btn-sm ${user.status ? 'btn-danger' : 'btn-success'}`}
              onClick={handleToggleStatus}
            >
              <i className={`bi ${user.status ? 'bi-slash-circle-fill' : 'bi-check-circle-fill'} me-2`}></i>
              {user.status ? 'Restrict Account' : 'Activate Account'}
            </button>
            <Link to="/admin/users" className="btn btn-sm btn-light">
              <i className="bi bi-arrow-left me-2"></i>Back
            </Link>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="customer-detail-layout">

            {/* ── Left Sidebar ── */}
            <div className="customer-detail-sidebar">
              {/* Profile card */}
              <div className="customer-sidebar-profile">
                <div className="customer-sidebar-cover">
                  <div className="customer-sidebar-avatar">
                    <img
                      src={user.picture || avatarSrc(user.name)}
                      alt={user.name}
                      onError={(e) => { e.target.src = avatarSrc(user.name); }}
                    />
                  </div>
                </div>
                <div className="customer-sidebar-info">
                  <div className="customer-sidebar-name">{user.name || 'N/A'}</div>
                  <div className="customer-sidebar-email">{user.email || ''}</div>
                  <span className={user.status ? 'user-badge-active' : 'user-badge-restricted'}>
                    {user.status ? 'Active' : 'Restricted'}
                  </span>
                </div>
              </div>

              {/* Tab nav */}
              <div className="customer-sidebar-nav">
                {TABS.map(({ key, label, icon }) => (
                  <button
                    key={key}
                    className={`customer-sidebar-nav-item ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    <i className={`bi ${icon}`}></i>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Right Content ── */}
            <div className="customer-detail-content">
              {renderContent()}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
